import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconLayoutDashboard,
  IconMap,
  IconSettings,
} from '@tabler/icons-react';

import { useUiState } from '@/hooks/useUiState';
import { useFormConflictDialog } from '@/hooks';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useIsMapView } from '@/hooks/useIsMapView';
import { usePresence } from '@/hooks/usePresence';
import { useLocationSharing } from '@/hooks/useLocationSharing';
import { useWorkspaceSwitcher } from '@/hooks/useWorkspaceSwitcher';
import { useMapViewMode } from '@/hooks/useMapViewMode';
import { useHideMobileChrome } from '@/hooks/useHideMobileChrome';
import {
  DEFAULT_SECTION,
  MAP_SECTIONS,
  SECTION_SEGMENT_INDEX,
  mapRoutes,
} from '@/routes/routes';
import { useGeoMarkupStore } from '@/store';
import { VIEW_MODE } from '@/types';
import {
  selectIsManagementPanelOpen,
  selectManagementPanelActiveTab,
  selectCurrentManagedWorkspace,
} from '@/store/selectors/workspaceSelectors';
import {
  selectUser,
  selectWorkspaces,
  selectCurrentWorkspaceId,
} from '@/store/selectors/authSelectors';
import { SessionExpiredModal } from '@/components/common/SessionExpiredModal';
import { WorkspaceModal } from '@/components/workspace/WorkspaceModal';
import { WorkspaceManagementPanel } from '@/components/workspace/WorkspaceManagementPanel';
import type { WorkspaceRole } from '@/types/workspace';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { Sidebar } from './Sidebar';
import {
  TopBar,
  TopBarActions,
  TopBarDivider,
  WorkspaceSelector,
} from './TopBar';
import { FloatingNavBar, MobileSidebarToggle } from './FloatingNavBar';
import { ArtifactSwitcher } from './TopBar/ArtifactSwitcher';
import type { NavItem } from './Sidebar/Sidebar.types';
import type { AppShellProps } from './AppShell.types';
import styles from './AppShell.module.css';

const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    icon: IconLayoutDashboard,
    labelKey: 'appShell.sidebar.dashboard',
    route: 'dashboard',
  },
  {
    id: 'map',
    icon: IconMap,
    labelKey: 'appShell.sidebar.map',
    route: 'map/tasks',
    // Legacy paths (tasks/notes) redirect to /map/* but keep matching here
    // so the nav item stays highlighted during the redirect.
    matchPaths: MAP_SECTIONS,
  },
  {
    id: 'settings',
    icon: IconSettings,
    labelKey: 'appShell.sidebar.settings',
    route: 'settings',
  },
];

export const AppShell = memo(function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const { shortId } = useParams<{ shortId: string }>();
  const location = useLocation();
  const { toggleSidePanel } = useUiState();
  // Single global unsaved-changes guard dialog. Driven by `formConflict.request`
  // (closing a form, opening another form, or switching workspace). Rendered
  // here so it works for switches triggered from the top bar, not just the map.
  const {
    showDialog: showUnsavedGuard,
    confirmSwitch: confirmUnsavedGuard,
    cancelSwitch: cancelUnsavedGuard,
  } = useFormConflictDialog();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { isMobile } = useDeviceLayout();
  const hideMobileChrome = useHideMobileChrome();
  const setCurrentWorkspace = useGeoMarkupStore(
    (state) => state.auth.setCurrentWorkspace
  );
  const currentUser = useGeoMarkupStore(selectUser);
  const workspaces = useGeoMarkupStore(selectWorkspaces);
  const currentWorkspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);
  const isManagementPanelOpen = useGeoMarkupStore(selectIsManagementPanelOpen);
  const managementPanelActiveTab = useGeoMarkupStore(
    selectManagementPanelActiveTab
  );
  const managedWorkspace = useGeoMarkupStore(selectCurrentManagedWorkspace);
  const closeManagementPanel = useGeoMarkupStore(
    (state) => state.workspace.closeManagementPanel
  );
  const setManagementTab = useGeoMarkupStore(
    (state) => state.workspace.setManagementTab
  );
  // App-wide presence connection (also exposes live-location senders)
  const { sendLocation, stopSharingLocation } = usePresence({
    workspaceId: currentWorkspaceId,
    enabled: !!currentWorkspaceId,
  });
  // App-wide live location sharing orchestrator (local watch + broadcast gate)
  useLocationSharing({ sendLocation, stopSharingLocation });

  useEffect(() => {
    const match = workspaces.find((ws) => ws.shortId === shortId);
    if (match && match.id !== currentWorkspaceId) {
      setCurrentWorkspace(match.id);
    }
  }, [shortId, workspaces, currentWorkspaceId, setCurrentWorkspace]);

  const showFloatingUI = !hideMobileChrome;

  // Active section derived from URL segment 3
  // (dashboard | map | settings | legacy tasks/notes).
  const activeSection = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts[SECTION_SEGMENT_INDEX] || DEFAULT_SECTION;
  }, [location.pathname]);

  const isMapSection = (MAP_SECTIONS as readonly string[]).includes(
    activeSection
  );
  const isSettingsView = activeSection === 'settings';

  const { view: activeView } = useMapViewMode();

  const isMapView = useIsMapView();
  const isNonMapArtifactView = isMapSection && activeView !== VIEW_MODE.MAP;

  const handleWorkspaceSwitch = useWorkspaceSwitcher();

  const handleMobileSidebarToggle = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  const handleMobileSidebarClose = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  const handleNavItemClick = useCallback(() => {
    handleMobileSidebarClose();
  }, [handleMobileSidebarClose]);

  const appShellClassName = isMapView
    ? `${styles.appShell} ${styles.appShellCompact}`
    : styles.appShell;

  function renderTopBarLayout(topBarProps: Parameters<typeof TopBar>[0]) {
    return (
      <>
        <div className={styles.topBarArea}>
          <TopBar
            {...topBarProps}
            onMobileMenuClick={handleMobileSidebarToggle}
          />
        </div>
        <main className={styles.mainArea}>{children || <Outlet />}</main>
      </>
    );
  }

  function renderMainContent() {
    if (isMapView) {
      return (
        <main className={styles.mainArea}>
          {showFloatingUI && <FloatingNavBar />}
          {showFloatingUI && (
            <MobileSidebarToggle
              onToggle={handleMobileSidebarToggle}
              onToggleSidePanel={toggleSidePanel}
            />
          )}
          {children || <Outlet />}
        </main>
      );
    }

    if (isNonMapArtifactView) {
      return renderTopBarLayout({
        actions: (
          <>
            {!isMobile && <ArtifactSwitcher />}
            {!isMobile && <TopBarDivider />}
            <ViewToggle />
            <TopBarDivider />
            <TopBarActions variant="compact" />
          </>
        ),
      });
    }

    return renderTopBarLayout({
      workspaceSelector: isSettingsView ? undefined : (
        <WorkspaceSelector onWorkspaceSwitch={handleWorkspaceSwitch} />
      ),
    });
  }

  // First-run gate: a workspace that has not configured its map yet is
  // redirected to the dedicated configurator page (a top-level sibling route,
  // so this never loops). Derived from the shortId-matched workspace to avoid
  // the currentWorkspaceId sync race (the sync effect lands a render later).
  const matchedWorkspace = workspaces.find((ws) => ws.shortId === shortId);
  if (shortId && matchedWorkspace && !matchedWorkspace.isMapConfigured) {
    return <Navigate to={mapRoutes.mapConfig(shortId)} replace />;
  }

  return (
    <div className={appShellClassName}>
      <div className={styles.sidebarArea}>
        <Sidebar
          items={NAV_ITEMS}
          activePath={activeSection}
          workspaceShortId={shortId || ''}
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={handleMobileSidebarClose}
          onNavItemClick={handleNavItemClick}
          isCompact={isMapView}
          showSidePanelToggle={isMapView && !isMobile}
        />
      </div>
      {renderMainContent()}
      <SessionExpiredModal />
      <WorkspaceModal />
      <WorkspaceManagementPanel
        opened={isManagementPanelOpen}
        workspace={managedWorkspace}
        currentUserRole={managedWorkspace?.role ?? ('Viewer' as WorkspaceRole)}
        currentUserId={currentUser?.id ?? ''}
        activeTab={
          managementPanelActiveTab as 'members' | 'invitations' | undefined
        }
        onClose={closeManagementPanel}
        onTabChange={setManagementTab}
      />
      <ConfirmationModal
        opened={showUnsavedGuard}
        onClose={cancelUnsavedGuard}
        onConfirm={confirmUnsavedGuard}
        title={t('dialogs.unsavedGuard.title')}
        message={t('dialogs.unsavedGuard.message')}
        variant="warning"
        confirmLabel={t('dialogs.unsavedGuard.confirm')}
        cancelLabel={t('dialogs.unsavedGuard.cancel')}
      />
    </div>
  );
});
